import { useState, useRef, useEffect } from "react";
import { Play, Pause, ChevronDown, CheckCircle, Users, Download, Share2, FileText, X, Loader2, ArrowRight, Scissors, Wrench, Briefcase, ChefHat, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { analytics } from "../../utils/analytics";

const samples = [
  {
    id: "contractor",
    label: "Contractor",
    icon: Wrench,
    title: "Missed Bid Call",
    audioSrc: "/audio/contractor.mp3",
    introText: "Hear how our AI handles an emergency plumbing call at 11pm:",
    transcript: [
      { speaker: "Customer", text: "Hey, this is Mike. I was calling about the bid for the kitchen remodel. I had a few questions about the materials list." },
      { speaker: "AI", text: "Hi Mike, thanks for calling! I've pulled up your bid for the kitchen remodel. What specific questions did you have about the materials?" }
    ]
  },
  {
    id: "salon",
    label: "Salon/Spa",
    icon: Scissors,
    title: "Booking Appointment",
    audioSrc: "/audio/salon.mp3",
    introText: "Listen to our AI booking a haircut appointment while the stylist is with a client:",
    transcript: [
      { speaker: "Customer", text: "Hi, I was hoping to book a haircut and color for next Tuesday anytime after 4pm." },
      { speaker: "AI", text: "I can help with that. Looking at the schedule, we have an opening with Sarah at 4:15pm or David at 5:00pm on Tuesday. Which works better?" }
    ]
  },
  {
    id: "restaurant",
    label: "Restaurant",
    icon: ChefHat,
    title: "Dinner Reservation",
    audioSrc: "/audio/restaurant.mp3",
    introText: "Hear our AI maximize table turnover by securing a specific reservation:",
    transcript: [
      { speaker: "Customer", text: "Hello, I'd like to make a reservation for a party of 4 this Saturday at 7:30pm. Do you have any tables available outside?" },
      { speaker: "AI", text: "We'd love to host you. I have a patio table available for 4 people at 7:30pm this Saturday. Shall I lock that in for you?" }
    ]
  },
  {
    id: "retail",
    label: "Retail",
    icon: ShoppingBag,
    title: "Product Inquiry",
    audioSrc: "/audio/retail.mp3",
    introText: "Listen to how the AI checks inventory and saves a sale automatically:",
    transcript: [
      { speaker: "Customer", text: "Hi there, I'm looking for the new running shoes in size 10. Do you have them in stock at the downtown store?" },
      { speaker: "AI", text: "Let me check our inventory... Yes, we have the new runners in size 10 at the downtown location. Would you like me to hold them for 24 hours?" }
    ]
  },
  {
    id: "consultant",
    label: "Consultant",
    icon: Briefcase,
    title: "Client Scheduling",
    audioSrc: "/audio/consultant.mp3",
    introText: "Our AI qualifying and booking a discovery call:",
    transcript: [
      { speaker: "Customer", text: "Good afternoon. This is Sarah calling to reschedule our strategy session. Would next Wednesday at 2pm work for you instead?" },
      { speaker: "AI", text: "Hi Sarah, no problem at all. I've moved your strategy session to next Wednesday at 2:00pm. You'll receive an updated calendar invite shortly." }
    ]
  },
];

const formatTime = (time: number) => {
  if (isNaN(time)) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const AudioSamples = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(samples[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  
  // Technical States
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [hasListened, setHasListened] = useState(false);

  // Audio state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Reset audio when industry changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      
      // Batch updates or delay to next frame to avoid potential synchronous layout thrashing/lint warnings
      // though typically safe, this is cleaner for the "cascading render" lint
      requestAnimationFrame(() => {
          setIsPlaying(false);
          setCurrentTime(0);
          setShowTranscript(false); 
          setAudioError(false);
          setAudioLoading(true); 
      });
      
      audioRef.current.load();
    }
  }, [selectedIndustry]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                console.error("Playback failed:", error);
                setAudioError(true);
                setShowTranscript(true);
            });
        }
        // Track play event
        analytics.customEvent('audio_sample_play', {
          industry: selectedIndustry.id,
          page_section: 'audio_samples'
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setAudioLoading(false);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setHasListened(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    // Track complete
    analytics.customEvent('audio_sample_complete', {
      industry: selectedIndustry.id,
      listen_duration: duration
    });
  };
  
  const handleError = () => {
      setAudioError(true);
      setAudioLoading(false);
      setShowTranscript(true); // Fallback
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressBarRef.current && audioRef.current && !audioError) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const width = rect.width;
      const percent = Math.min(Math.max(x / width, 0), 1);
      const newTime = percent * duration;

      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleIndustryChange = (sample: typeof samples[0]) => {
    analytics.customEvent('audio_industry_switch', {
      from_industry: selectedIndustry.id,
      to_industry: sample.id
    });
    setSelectedIndustry(sample);
    setIsOpen(false);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = selectedIndustry.audioSrc;
    link.download = `NorthForge_${selectedIndustry.label}_Sample.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    analytics.customEvent('audio_download', { industry: selectedIndustry.id });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'North Forge AI Audio Sample',
        text: `Listen to how North Forge AI handles calls for ${selectedIndustry.label} businesses.`,
        url: window.location.href,
      }).catch(console.error);
    } else {
      // Fallback
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
    analytics.customEvent('audio_share', { industry: selectedIndustry.id });
  };

  // Track section view
  useEffect(() => {
      const observer = new IntersectionObserver(
          (entries) => {
              if (entries[0].isIntersecting) {
                  analytics.viewedSection('audio_samples');
                  observer.disconnect(); // Track only once per session
              }
          },
          { threshold: 0.5 } // Trigger when 50% visible
      );
      
      const section = document.getElementById('audio-samples');
      if (section) {
          observer.observe(section);
      }
      
      return () => observer.disconnect();
  }, []);

  return (
    <section id="audio-samples" className="py-20 bg-brand-dark relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4 font-display text-white">
             "Wait, That Actually <span className="text-brand-accent">Sounds Human?"</span>
            </h2>
            <p className="text-xl text-brand-primary mb-2 font-medium">
                Listen to real AI conversations from businesses like yours
            </p>
            <p className="text-slate-400">
                No scripts. No robots. Just natural conversation.
            </p>
        </div>
        
        {/* Social Proof Badge */}
        <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-base/50 border border-brand-border text-sm text-slate-300">
                <Users className="w-4 h-4 text-brand-primary" />
                <span>Listened by <span className="text-white font-bold">1,247+</span> business owners this month</span>
            </div>
        </div>

        <div className="bg-brand-base/30 border border-brand-border rounded-2xl p-6 md:p-8 backdrop-blur-md max-w-2xl mx-auto shadow-2xl">
          {/* Industry Dropdown */}
          <div className="relative mb-6 text-left max-w-xs mx-auto">
            <label className="block text-sm font-medium text-slate-400 mb-2 text-center">
              Select your business type
            </label>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between bg-brand-dark border border-brand-border rounded-lg px-4 py-3 text-white font-medium hover:border-brand-primary/50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <selectedIndustry.icon className="w-4 h-4 text-brand-primary" />
                {selectedIndustry.label}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-brand-dark border border-brand-border rounded-lg shadow-xl z-20 overflow-hidden"
                >
                  {samples.map((sample) => (
                    <button
                      key={sample.id}
                      onClick={() => handleIndustryChange(sample)}
                      className="w-full text-left px-4 py-3 hover:bg-brand-primary/10 text-slate-300 hover:text-white transition-colors flex items-center justify-between group"
                    >
                      <span className="flex items-center gap-2">
                          <sample.icon className="w-4 h-4 text-slate-500 group-hover:text-brand-primary transition-colors"/>
                          {sample.label}
                      </span>
                      {selectedIndustry.id === sample.id && (
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-primary" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Intro Text */}
          <div className="mb-4 text-center">
             <p className="text-slate-300 italic">"{selectedIndustry.introText}"</p>
          </div>

          {/* Audio Player */}
          <div className="bg-brand-dark border border-brand-border rounded-xl p-6 relative">
             {/* Quality Badge */}
             <div className="absolute top-0 right-0 -mt-3 -mr-2 bg-brand-base border border-brand-border px-2 py-0.5 rounded text-[10px] text-brand-accent flex items-center gap-1 shadow-sm">
                 <CheckCircle className="w-3 h-3" />
                 Actual AI Quality
             </div>
            
            {audioError && (
                <div className="absolute inset-0 bg-brand-dark/90 z-10 flex flex-col items-center justify-center text-center p-4 rounded-xl">
                    <p className="text-red-400 text-sm mb-2 font-medium">Audio temporarily unavailable</p>
                    <p className="text-xs text-slate-400">Please read the transcript below</p>
                </div>
            )}

            <div className="flex items-center gap-6">
                <audio
                    ref={audioRef}
                    src={selectedIndustry.audioSrc}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleEnded}
                    onError={handleError}
                    onLoadStart={() => setAudioLoading(true)}
                    onCanPlay={() => setAudioLoading(false)}
                    className="hidden"
                    playsInline
                />

                <button
                onClick={togglePlay}
                disabled={audioLoading || audioError}
                className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center flex-shrink-0 transition-all shadow-lg shadow-brand-primary/20 group ${audioLoading ? 'bg-brand-base cursor-wait' : 'bg-brand-primary hover:bg-brand-accent cursor-pointer'}`}
                >
                {audioLoading ? (
                    <Loader2 className="w-6 h-6 md:w-8 md:h-8 text-brand-primary animate-spin" />
                ) : isPlaying ? (
                    <Pause className="w-6 h-6 md:w-8 md:h-8 text-brand-dark fill-current" />
                ) : (
                    <Play className="w-6 h-6 md:w-8 md:h-8 text-brand-dark fill-current ml-1 group-hover:scale-110 transition-transform" />
                )}
                </button>
                <div className="flex-1 space-y-2">
                <div className="flex justify-between text-sm font-medium text-slate-300">
                    <span>{selectedIndustry.title}</span>
                    <span>
                    {formatTime(currentTime)} / {formatTime(duration || 0)}
                    </span>
                </div>
                {/* Progress Bar */}
                <div
                    ref={progressBarRef}
                    onClick={handleSeek}
                    className={`h-2 bg-brand-base rounded-full overflow-hidden ${audioError ? 'cursor-not-allowed opacity-50' : 'cursor-pointer group'}`}
                >
                    <div
                    className="h-full bg-brand-primary relative transition-all duration-100"
                    style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
                    >
                    {/* Handle */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity translate-x-1.5" />
                    </div>
                </div>
                </div>
            </div>
          
            {/* Player Controls / Extras */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-brand-border/50">
                <button 
                  onClick={() => setShowTranscript(!showTranscript)}
                  className="text-xs flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
                >
                    {showTranscript ? <X className="w-3 h-3"/> : <FileText className="w-3 h-3"/>}
                    {showTranscript ? "Hide Transcript" : "View Transcript"}
                </button>
                
                <div className="flex items-center gap-3">
                    <button 
                      onClick={handleDownload}
                      className="text-xs flex items-center gap-1 text-slate-400 hover:text-brand-primary transition-colors"
                      title="Download Sample"
                    >
                        <Download className="w-3 h-3" />
                         <span className="hidden sm:inline">Download</span>
                    </button>
                    <button 
                      onClick={handleShare}
                      className="text-xs flex items-center gap-1 text-slate-400 hover:text-brand-primary transition-colors"
                      title="Share"
                    >
                        <Share2 className="w-3 h-3" />
                        <span className="hidden sm:inline">Share</span>
                    </button>
                </div>
            </div>

            {/* Transcript Panel */}
            <AnimatePresence>
                {showTranscript && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="mt-4 p-3 bg-brand-base/50 rounded-lg text-xs space-y-2 border border-brand-border/50 text-left">
                            {selectedIndustry.transcript.map((line, idx) => (
                                <div key={idx} className="flex gap-2">
                                    <span className={`font-bold w-16 flex-shrink-0 ${line.speaker === 'AI' ? 'text-brand-primary' : 'text-slate-400'}`}>
                                        {line.speaker}:
                                    </span>
                                    <span className="text-slate-300">{line.text}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
             </AnimatePresence>
          </div>

          {/* Comparison Section (CRO) */}
          <div className="bg-brand-dark/50 p-4 rounded-lg mt-6 border border-brand-border/30">
            <p className="text-xs text-slate-400 mb-3 uppercase tracking-wider text-center">The Difference is Clear</p>
            <div className="flex gap-4">
                <div className="flex-1 text-center sm:text-left">
                    <p className="text-xs text-red-400 font-bold mb-1">❌ Your Voicemail</p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                        "Please leave a message..."<br/>
                        <span className="text-red-400/70">↓ Customer hangs up</span><br/>
                        Lost opportunity
                    </p>
                </div>
                <div className="w-px bg-brand-border/50" />
                <div className="flex-1 text-center sm:text-left">
                    <p className="text-xs text-green-400 font-bold mb-1">✓ North Forge AI</p>
                    <p className="text-xs text-slate-300 leading-relaxed">
                        Answers instantly 24/7<br/>
                        <span className="text-green-400/70">↓ Appointment booked</span><br/>
                        Revenue captured
                    </p>
                </div>
            </div>
          </div>

          {/* Objection Crusher */}
          <div className="mt-6 text-center">
            <details className="cursor-pointer group inline-block text-left">
                <summary className="text-xs text-slate-500 hover:text-brand-primary transition-colors list-none flex items-center gap-1 justify-center">
                    <span className="border-b border-dashed border-slate-600 group-hover:border-brand-primary">Will customers know it's AI?</span>
                    <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform"/>
                </summary>
                <div className="mt-2 text-xs text-slate-400 max-w-sm mx-auto bg-brand-dark p-3 rounded border border-brand-border">
                    Only if they ask. In practice, <span className="text-white font-medium">94%</span> of calls complete naturally without the caller realizing. Those who do notice appreciate the instant service.
                </div>
            </details>
          </div>
          
            {/* Post-Listen CTA - Dynamic */}
            <AnimatePresence>
            {hasListened && (
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8 text-center"
                >
                    <p className="text-brand-accent font-medium mb-4">
                    Impressed? Imagine this answering YOUR calls.
                    </p>
                    <button 
                        onClick={() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' })}
                        className="inline-flex items-center px-6 py-3 bg-brand-primary text-brand-dark font-bold rounded-full hover:bg-brand-accent transition-colors"
                    >
                    See How Much You're Losing
                    <ArrowRight className="ml-2 w-5 h-5" />
                    </button>
                </motion.div>
            )}
            </AnimatePresence>

        </div>
      </div>
    </section>
  );
};
